import { toast } from "sonner";
import { useEffect, useRef, useState } from "react";
import { Layout } from "../../components/layout/Layout";
import { EtiquettesTabs } from "../../components/EtiquettesTabs";
import { Printer, Download, FileDown, RotateCcw } from "lucide-react";

import { PDFJS_SRC, PDFJS_WORKER_SRC, DEFAULT_CROP, LABEL_WIDTH_MM, LABEL_HEIGHT_MM, loadScript, printLabelImage, composeLabelFromPdfPage, composeLabelFromCanvas } from "../../lib/depotLabelPrint";

const JSPDF_SRC = "https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js";

export default function DepotLabelPage() {
  const sourceCanvasRef = useRef(null);
  const fileInputRef = useRef(null);
  const croppedCanvasRef = useRef(null);
  const fileNameRef = useRef("etiquette");
  const pdfPageRef = useRef(null); // page PDF d'origine, pour un rendu d'impression à 203 dpi

  const [ready, setReady] = useState(false);
  const [active, setActive] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [meta, setMeta] = useState({ dims: "—", file: "—" });
  const [previewSrc, setPreviewSrc] = useState(null);

  useEffect(() => {
    (async () => {
      await loadScript(PDFJS_SRC);
      if (window.pdfjsLib) window.pdfjsLib.GlobalWorkerOptions.workerSrc = PDFJS_WORKER_SRC;
      await loadScript(JSPDF_SRC);
      setReady(true);
    })();
  }, []);

  const renderPdf = async (arrayBuffer) => {
    const pdf = await window.pdfjsLib.getDocument({ data: arrayBuffer }).promise;
    const page = await pdf.getPage(1);
    pdfPageRef.current = page;
    const scale = 2.2;
    const viewport = page.getViewport({ scale });
    const canvas = sourceCanvasRef.current;
    canvas.width = viewport.width;
    canvas.height = viewport.height;
    const ctx = canvas.getContext("2d");
    await page.render({ canvasContext: ctx, viewport }).promise;
  };

  const renderImage = (img) => {
    const canvas = sourceCanvasRef.current;
    canvas.width = img.naturalWidth;
    canvas.height = img.naturalHeight;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(img, 0, 0);
  };

  const cropWithFraction = (frac) => {
    const source = sourceCanvasRef.current;
    const sw = source.width, sh = source.height;
    const sx = frac.x0 * sw, sy = frac.y0 * sh;
    const cw = (frac.x1 - frac.x0) * sw, ch = (frac.y1 - frac.y0) * sh;

    const cropped = document.createElement("canvas");
    cropped.width = Math.round(cw);
    cropped.height = Math.round(ch);
    const ctx = cropped.getContext("2d");
    ctx.drawImage(source, sx, sy, cw, ch, 0, 0, cropped.width, cropped.height);
    croppedCanvasRef.current = cropped;
    setPreviewSrc(cropped.toDataURL("image/png"));
    setMeta({ dims: `${cropped.width} × ${cropped.height} px`, file: fileNameRef.current });
  };

  const handleFile = async (file) => {
    fileNameRef.current = file.name || "etiquette";
    pdfPageRef.current = null;
    if (file.type === "application/pdf") {
      const buf = await file.arrayBuffer();
      await renderPdf(buf);
    } else {
      const url = URL.createObjectURL(file);
      const img = new Image();
      await new Promise((res, rej) => { img.onload = res; img.onerror = rej; img.src = url; });
      renderImage(img);
    }
    setActive(true);
  };

  useEffect(() => {
    if (active && sourceCanvasRef.current?.width) {
      cropWithFraction(DEFAULT_CROP);
    }
  }, [active]);

  const onInputChange = (e) => { if (e.target.files[0]) handleFile(e.target.files[0]); };
  const onDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) handleFile(file);
  };

  // Calcule les dimensions (mm) à donner à l'image pour qu'elle tienne
  // entièrement dans le format d'étiquette LABEL_WIDTH_MM x LABEL_HEIGHT_MM
  // sans être déformée (mise à l'échelle uniforme, façon `object-fit:
  // contain`), ainsi que le décalage nécessaire pour la centrer.
  const fitToLabel = (pixelWidth, pixelHeight) => {
    const canvasRatio = pixelWidth / pixelHeight;
    const labelRatio = LABEL_WIDTH_MM / LABEL_HEIGHT_MM;
    let width, height;
    if (canvasRatio > labelRatio) {
      width = LABEL_WIDTH_MM;
      height = LABEL_WIDTH_MM / canvasRatio;
    } else {
      height = LABEL_HEIGHT_MM;
      width = LABEL_HEIGHT_MM * canvasRatio;
    }
    return { width, height, offsetX: (LABEL_WIDTH_MM - width) / 2, offsetY: (LABEL_HEIGHT_MM - height) / 2 };
  };

  const doPrint = async () => {
    if (!croppedCanvasRef.current) return;
    // Impression : rendu net 203 dpi, 101 x 150 mm, noir et blanc (PDF), ou mise
    // en page équivalente de l'image importée.
    const sheet = pdfPageRef.current
      ? await composeLabelFromPdfPage(pdfPageRef.current)
      : composeLabelFromCanvas(croppedCanvasRef.current);
    const res = await printLabelImage(sheet.toDataURL("image/png"));
    if (!res.ok) toast.error(res.error || "Échec de l'impression");
    else if (window.SogoDesktop?.printImage) toast.success("Étiquette envoyée à l'imprimante");
  };

  const downloadPng = () => {
    if (!croppedCanvasRef.current) return;
    const a = document.createElement("a");
    a.download = `${fileNameRef.current.replace(/\.[^.]+$/, "")}-etiquette.png`;
    a.href = croppedCanvasRef.current.toDataURL("image/png");
    a.click();
  };

  const downloadPdf = () => {
    if (!croppedCanvasRef.current) return;
    const canvas = croppedCanvasRef.current;
    const { jsPDF } = window.jspdf;
    const { width, height, offsetX, offsetY } = fitToLabel(canvas.width, canvas.height);
    const pdf = new jsPDF({ orientation: "portrait", unit: "mm", format: [LABEL_WIDTH_MM, LABEL_HEIGHT_MM] });
    pdf.addImage(canvas.toDataURL("image/png"), "PNG", offsetX, offsetY, width, height);
    pdf.save(`${fileNameRef.current.replace(/\.[^.]+$/, "")}-etiquette.pdf`);
  };

  const reset = () => {
    setActive(false);
    setPreviewSrc(null);
    croppedCanvasRef.current = null;
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  return (
    <Layout title="Dépôt — Étiquettes">
      <EtiquettesTabs />
      <div className="max-w-2xl">
        <canvas ref={sourceCanvasRef} className="hidden" />
        {!active && (
          <label
            htmlFor="etiquette-file-input"
            data-testid="etiquette-dropzone"
            onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
            onDragLeave={() => setDragging(false)}
            onDrop={onDrop}
            className={`block border-2 border-dashed rounded-lg p-12 text-center cursor-pointer transition-colors duration-200 ${dragging ? "border-primary bg-primary/5" : "border-border bg-card"}`}
          >
            <span className="inline-block text-xs font-mono border border-foreground/40 rounded px-2 py-1 mb-4">PDF / JPG / PNG</span>
            <p className="font-semibold mb-1">Dépose ton bordereau Chronopost ici</p>
            <p className="text-sm text-muted-foreground">ou clique pour choisir un fichier — la zone encadrée en rouge est détectée automatiquement</p>
            <input
              id="etiquette-file-input"
              ref={fileInputRef}
              data-testid="etiquette-file-input"
              type="file"
              accept="application/pdf,image/png,image/jpeg"
              className="hidden"
              disabled={!ready}
              onChange={onInputChange}
            />
          </label>
        )}

        {active && (
          <div className="rounded-xl border border-border bg-card p-6" data-testid="etiquette-workarea">
            <div className="flex items-center justify-between mb-4">
              <p className="text-sm font-semibold uppercase tracking-widest text-muted-foreground">Étiquette découpée automatiquement</p>
              <button onClick={reset} data-testid="etiquette-reset-button" className="text-xs text-muted-foreground underline flex items-center gap-1">
                <RotateCcw size={12} /> recommencer
              </button>
            </div>
            <div className="rounded-lg border border-border bg-secondary/40 p-4 flex items-center justify-center min-h-[220px] mb-4" data-testid="etiquette-preview">
              {previewSrc ? (
                <img src={previewSrc} alt="Étiquette découpée" className="max-w-full max-h-[340px] shadow-lg" />
              ) : (
                <p className="text-xs font-mono text-muted-foreground">Traitement en cours…</p>
              )}
            </div>
            <div className="flex flex-wrap gap-2 mb-4">
              <button onClick={doPrint} disabled={!previewSrc} data-testid="etiquette-print-button" className="px-4 py-3 rounded-md bg-primary text-primary-foreground font-semibold text-sm disabled:opacity-40 flex items-center gap-2">
                <Printer size={15} /> Imprimer
              </button>
              <button onClick={downloadPng} disabled={!previewSrc} data-testid="etiquette-download-png-button" className="px-4 py-3 rounded-md border border-foreground/30 font-semibold text-sm disabled:opacity-40 flex items-center gap-2">
                <Download size={15} /> Télécharger en image (PNG)
              </button>
              <button onClick={downloadPdf} disabled={!previewSrc} data-testid="etiquette-download-pdf-button" className="px-4 py-3 rounded-md border border-foreground/30 font-semibold text-sm disabled:opacity-40 flex items-center gap-2">
                <FileDown size={15} /> Télécharger en PDF
              </button>
            </div>
            <div className="flex justify-between text-xs font-mono text-muted-foreground border-t border-dashed border-border pt-3">
              <span data-testid="etiquette-meta-dims">{meta.dims}</span>
              <span data-testid="etiquette-meta-file">{meta.file}</span>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}
